import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack, Paper, Grid,
  IconButton, Tooltip, Divider, Link, LinearProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// 테스트 품질 현황 (Test Quality) — 팀이 관리하는 모든 코드 저장소의 테스트 실행·
// 커버리지를 한 화면에. 데이터: GET /api/quality/portfolio
//
// 이 화면이 답하려는 질문은 넷이다.
//   ① 지금 얼마나 재고 있나 (측정 커버리지 — 커버리지 수치보다 먼저다)
//   ② 깨져 있는 테스트가 얼마나 되나 (실패 건수)
//   ③ 커버리지는 얼마나 되나
//   ④ 못 재는 곳은 어디이고 왜인가 (조치 대상)
//
// ⚠️ 설계 의도: "측정 실패"와 "테스트 없음"을 절대 뭉뚱그리지 않는다.
//    전자는 우리(플랫폼)가 고칠 파이프라인 문제, 후자는 제품팀이 채울 테스트 문제다.
//    조치 주체가 다르므로 화면에서도 분리한다.

const STATUS_META = {
  'ok': { label: '측정됨', tone: 'success' },
  'ok-tests-failed': { label: '측정됨 · 테스트 실패', tone: 'warning' },
  'deps-install-failed': { label: '의존성 설치 실패', tone: 'error' },
  'coverage-provider-missing': { label: '커버리지 도구 없음', tone: 'error' },
  'error': { label: '측정 오류', tone: 'error' },
  'no-tests': { label: '테스트 없음', tone: 'default' },
  'unsupported': { label: '미지원 생태계', tone: 'default' },
  'not-installed': { label: '모듈 미설치', tone: 'default' },
  'fetch-error': { label: '조회 실패', tone: 'error' },
};

const num = (n) => (n ?? 0).toLocaleString();
const pctText = (n) => (typeof n === 'number' ? `${n}%` : '—');

function toneColor(theme, tone) {
  const p = theme.palette;
  switch (tone) {
    case 'success': return p.success.main;
    case 'warning': return p.warning.main;
    case 'error': return p.error.main;
    default: return p.text.disabled;
  }
}

// 커버리지 색 — 절대 기준. 팀 목표는 60%(성과목표 지표 1-1).
function covColor(theme, v) {
  if (typeof v !== 'number') return theme.palette.text.disabled;
  if (v >= 60) return theme.palette.success.main;
  if (v >= 30) return theme.palette.warning.main;
  return theme.palette.error.main;
}

function StatCard({ label, value, sub, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ mt: 0.5, color: color || 'text.primary', fontWeight: 600 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

export default function QualityPage() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const res = await fetch('/api/quality/portfolio');
      if (!res.ok) throw new Error(`API ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const t = data?.totals;

  // 측정률 — 이 화면의 첫 번째 숫자. 커버리지 평균은 측정된 repo 안에서만 의미가 있으므로
  // 측정률이 낮으면 평균 자체를 신뢰할 수 없다는 것을 먼저 보여준다.
  const measureRate = useMemo(() => {
    if (!t?.repos) return null;
    return Math.round((t.measured / t.repos) * 1000) / 10;
  }, [t]);

  // 조치 대상 — 못 재는 repo 를 사유별로 묶는다. 이게 개선 백로그다.
  const blockedRepos = useMemo(() => {
    if (!data?.repos) return [];
    return data.repos
      .filter((r) => ['deps-install-failed', 'coverage-provider-missing', 'error', 'fetch-error'].includes(r.status))
      .sort((a, b) => String(a.status).localeCompare(String(b.status)));
  }, [data]);

  const measuredRepos = useMemo(() => {
    if (!data?.repos) return [];
    return data.repos
      .filter((r) => ['ok', 'ok-tests-failed'].includes(r.status))
      .sort((a, b) => (a.coverage?.lines ?? 999) - (b.coverage?.lines ?? 999));
  }, [data]);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>테스트 품질 현황</Typography>
        <Tooltip title="새로고침">
          <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        각 코드 저장소가 주 1회 스스로 테스트를 실행해 남긴 스냅샷을 모은 것입니다.
        {data?.generated_at && ` · 집계 ${String(data.generated_at).slice(0, 16).replace('T', ' ')}`}
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>불러오지 못했습니다: {err}</Alert>}

      {t && (
        <>
          {/* 조회 실패는 데이터 신뢰성 문제다 — 측정률 경고보다 먼저 알린다.
              이 값이 0이 아니면 아래 모든 수치가 실제보다 낮게 나온다. */}
          {t.fetch_errors > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              스냅샷 <b>조회에 실패한 저장소가 {num(t.fetch_errors)}개</b> 있습니다 —
              아래 수치는 실제보다 낮게 집계됐을 수 있습니다.
              «모듈 미설치»와는 다른 문제이며, GitHub 조회 한도·권한을 확인해야 합니다.
              {data.diagnostics?.snapshot_errors?.length > 0 && (
                <Box component="span" sx={{ display: 'block', mt: 0.5, fontSize: '0.85em', opacity: 0.9 }}>
                  {data.diagnostics.snapshot_errors.slice(0, 3).join(' · ')}
                  {data.diagnostics.snapshot_errors.length > 3 && ` 외 ${data.diagnostics.snapshot_errors.length - 3}건`}
                </Box>
              )}
            </Alert>
          )}

          {/* 측정률이 낮을 때는 평균 커버리지를 신뢰하지 말라고 명시한다. */}
          {measureRate !== null && measureRate < 70 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              전체 {num(t.repos)}개 저장소 중 <b>{num(t.measured)}개({measureRate}%)</b>만 실제로 측정됐습니다.
              아래 평균 커버리지는 <b>측정된 저장소 안에서의 값</b>이라 전체를 대표하지 않습니다.
              먼저 «못 재는 곳»을 줄이는 것이 우선입니다.
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                label="측정률"
                value={measureRate === null ? '—' : `${measureRate}%`}
                sub={`${num(t.measured)} / ${num(t.repos)} 저장소`}
                color={covColor(theme, measureRate)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="평균 커버리지 (측정분)"
                value={pctText(t.coverage_avg)}
                sub="목표 60% · 단순 평균(코드량 미가중)"
                color={covColor(theme, t.coverage_avg)}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="실패 중인 테스트"
                value={num(t.tests_failed)}
                sub={`전체 ${num(t.tests_total)}건 중`}
                color={t.tests_failed > 0 ? theme.palette.error.main : theme.palette.success.main}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="못 재는 저장소"
                value={num(t.blocked)}
                sub={`미설치 ${num(t.not_installed)}개 별도`}
                color={t.blocked > 0 ? theme.palette.warning.main : theme.palette.success.main}
              />
            </Grid>
          </Grid>

          {/* ── 조치 대상: 못 재는 곳 ── */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              못 재는 저장소 — 조치 대상
            </Typography>
            <Typography variant="caption" color="text.secondary">
              측정 파이프라인이 막힌 곳입니다. 테스트가 없는 것과는 다른 문제이며, 대개 플랫폼 쪽에서 풀립니다.
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {blockedRepos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">막힌 저장소 없음 🎉</Typography>
            ) : (
              <Stack spacing={1}>
                {blockedRepos.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  return (
                    <Stack key={r.repo} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip size="small" label={meta.label}
                        sx={{ bgcolor: toneColor(theme, meta.tone), color: '#fff', fontWeight: 600 }} />
                      <Link href={`https://github.com/rayrndsw/${r.repo}`} target="_blank" rel="noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                        {r.repo}<OpenInNewIcon sx={{ fontSize: 13 }} />
                      </Link>
                      <Typography variant="caption" color="text.secondary">{r.product_name}</Typography>
                      {r.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                          {r.reason}
                        </Typography>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>

          {/* ── 측정된 저장소 (커버리지 낮은 순) ── */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              측정된 저장소 — 커버리지 낮은 순
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {measuredRepos.length === 0 ? (
              <Typography variant="body2" color="text.secondary">아직 측정된 저장소가 없습니다.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {measuredRepos.map((r) => (
                  <Box key={r.repo}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                      <Link href={`https://github.com/rayrndsw/${r.repo}`} target="_blank" rel="noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, fontWeight: 600 }}>
                        {r.repo}<OpenInNewIcon sx={{ fontSize: 13 }} />
                      </Link>
                      <Typography variant="caption" color="text.secondary">
                        {r.product_name} · {r.runner || '—'}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {r.tests?.failed > 0 && (
                        <Chip size="small" label={`${r.tests.failed}건 실패`}
                          sx={{ bgcolor: theme.palette.error.main, color: '#fff', fontWeight: 600 }} />
                      )}
                      <Typography variant="body2" sx={{ fontWeight: 700, color: covColor(theme, r.coverage?.lines), minWidth: 52, textAlign: 'right' }}>
                        {pctText(r.coverage?.lines)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, r.coverage?.lines ?? 0))}
                      sx={{
                        height: 6, borderRadius: 3,
                        '& .MuiLinearProgress-bar': { backgroundColor: covColor(theme, r.coverage?.lines) },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>

          {/* ── 제품별 ── */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>제품별 현황</Typography>
            <Typography variant="caption" color="text.secondary">실패 테스트가 많은 순</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack spacing={1}>
              {(data.products || []).map((p) => (
                <Stack key={p.id} direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130 }}>
                    저장소 {num(p.repo_count)} · 측정 {num(p.measured)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: covColor(theme, p.coverage_avg), fontWeight: 600, minWidth: 60 }}>
                    {pctText(p.coverage_avg)}
                  </Typography>
                  {p.tests_failed > 0 && (
                    <Chip size="small" label={`실패 ${num(p.tests_failed)}`}
                      sx={{ bgcolor: theme.palette.error.main, color: '#fff' }} />
                  )}
                </Stack>
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </Box>
  );
}
